/**
 * El carrito: botón flotante + panel.
 *
 * No hay checkout. El carrito arma un pedido y lo manda por WhatsApp, que es
 * donde la dueña cobra (transferencia o tarjeta, lo lleva ella). Por eso el botón
 * final dice "Proceder al pago por WhatsApp" y no "Pagar": en esta página no se
 * paga nada, y prometerlo sería mentirle al visitante.
 */

import { useEffect } from "react";
import { X, ShoppingBag, Trash2, Minus, Plus } from "lucide-react";
import { formatearCorto } from "@/lib/precio";
import { waLink } from "@/data/site";

const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 0 1 6.988 2.896 9.82 9.82 0 0 1 2.893 6.994c-.003 5.45-4.437 9.886-9.885 9.886" />
  </svg>
);

/** Fila de un producto dentro del panel. */
function Linea({ item, onCantidad, onQuitar }) {
  const { producto: p, qty, subtotalCents } = item;
  return (
    <li className="flex gap-4 py-4 border-b border-[#1a1a1a]" data-testid={`carrito-linea-${p.id}`}>
      <img
        src={p.img}
        alt=""
        width={64}
        height={85}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        className="w-16 h-[85px] object-cover flex-shrink-0 border border-[#1a1a1a]"
      />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#d6ad60]">{p.brand}</p>
        <p className="font-serif-lux text-base leading-tight truncate">{p.name}</p>
        <p className="text-xs text-neutral-500 mt-0.5">{formatearCorto(p.priceCents)} c/u</p>

        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center border border-[#d6ad60]/30">
            <button
              type="button"
              onClick={() => onCantidad(p.id, qty - 1)}
              aria-label={`Quitar una pieza de ${p.brand} ${p.name}`}
              className="px-2 py-1 text-[#d6ad60] hover:bg-[#d6ad60] hover:text-[#050505] transition-colors"
              data-testid={`carrito-menos-${p.id}`}
            >
              <Minus className="w-3 h-3" />
            </button>
            <span
              className="px-3 text-sm tabular-nums min-w-[2ch] text-center"
              data-testid={`carrito-qty-${p.id}`}
            >
              {qty}
            </span>
            <button
              type="button"
              onClick={() => onCantidad(p.id, qty + 1)}
              aria-label={`Agregar una pieza de ${p.brand} ${p.name}`}
              className="px-2 py-1 text-[#d6ad60] hover:bg-[#d6ad60] hover:text-[#050505] transition-colors"
              data-testid={`carrito-mas-${p.id}`}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => onQuitar(p.id)}
            aria-label={`Quitar ${p.brand} ${p.name} del carrito`}
            className="text-neutral-600 hover:text-neutral-300 transition-colors"
            data-testid={`carrito-quitar-${p.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="font-serif-lux text-[#e5c37a] whitespace-nowrap" data-testid={`carrito-subtotal-${p.id}`}>
        {formatearCorto(subtotalCents)}
      </p>
    </li>
  );
}

export default function Carrito({ resumen, mensaje, abierto, setAbierto, onCantidad, onQuitar, onVaciar }) {
  const { items, unidades, subtotalCents, descuentoCents, totalCents } = resumen;

  // Esc cierra: el panel tapa la pantalla en el celular y quedarse encerrado en él
  // es la forma más rápida de que alguien abandone el sitio.
  useEffect(() => {
    if (!abierto) return undefined;
    const onKey = (e) => e.key === "Escape" && setAbierto(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [abierto, setAbierto]);

  return (
    <>
      {/* Botón flotante. Sólo aparece con algo adentro: un carrito vacío flotando
          sobre un sitio de perfumería es ruido. */}
      {unidades > 0 && !abierto && (
        <button
          type="button"
          onClick={() => setAbierto(true)}
          /* bottom-24 y no bottom-6: el botón flotante de WhatsApp ya ocupa esa
             esquina. Apilados, no encimados. */
          className="fixed bottom-24 right-6 z-40 flex items-center gap-2 bg-[#d6ad60] text-[#050505] pl-4 pr-5 py-3 shadow-lg shadow-black/50 hover:bg-[#e5c37a] transition-colors"
          data-testid="carrito-flotante"
          aria-label={`Ver el carrito: ${unidades} ${unidades === 1 ? "pieza" : "piezas"}`}
        >
          <ShoppingBag className="w-5 h-5" />
          <span className="text-xs uppercase tracking-widest font-medium">
            {unidades} {unidades === 1 ? "pieza" : "piezas"}
          </span>
          <span className="font-serif-lux text-sm" data-testid="carrito-flotante-total">
            {formatearCorto(totalCents)}
          </span>
        </button>
      )}

      {abierto && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="carrito-panel">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setAbierto(false)}
            aria-hidden="true"
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Tu selección"
            className="relative w-full sm:max-w-md bg-[#0a0a0a] border-l border-[#d6ad60]/20 flex flex-col max-h-full"
          >
            <header className="flex items-center justify-between px-6 py-5 border-b border-[#1a1a1a]">
              <h2 className="font-serif-lux text-xl text-gold-gradient">Tu selección</h2>
              <button
                type="button"
                onClick={() => setAbierto(false)}
                aria-label="Cerrar el carrito"
                className="text-neutral-400 hover:text-white transition-colors"
                data-testid="carrito-cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
                <ShoppingBag className="w-10 h-10 text-[#d6ad60]/40 mb-4" />
                <p className="text-neutral-400 text-sm" data-testid="carrito-vacio">
                  Todavía no elegiste ningún perfume.
                </p>
              </div>
            ) : (
              <>
                <ul className="flex-1 overflow-y-auto px-6">
                  {items.map((item) => (
                    <Linea key={item.producto.id} item={item} onCantidad={onCantidad} onQuitar={onQuitar} />
                  ))}
                </ul>

                <div className="px-6 py-5 border-t border-[#1a1a1a] space-y-2">
                  {/* El aviso de "te faltan N" sólo aparece cuando falta de verdad:
                      empujar a comprar más cuando el descuento ya está ganado es
                      ruido, y cuando no hay promo activa sería una promesa falsa. */}
                  {resumen.proximo && (
                    <p
                      className="text-xs text-[#d6ad60] border border-[#d6ad60]/30 px-3 py-2 mb-3"
                      data-testid="carrito-faltan"
                    >
                      Agregá {resumen.proximo.faltan}{" "}
                      {resumen.proximo.faltan === 1 ? "pieza más" : "piezas más"} y obtenés{" "}
                      {resumen.proximo.percent}% de descuento.
                    </p>
                  )}

                  {descuentoCents > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-neutral-400">
                        <span>Subtotal</span>
                        <span data-testid="carrito-subtotal">{formatearCorto(subtotalCents)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-[#d6ad60]">
                        <span>
                          Descuento {resumen.promoPercent}% ({resumen.tramo.minUnits}+ piezas)
                        </span>
                        <span data-testid="carrito-descuento">−{formatearCorto(descuentoCents)}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-baseline pt-2">
                    <span className="text-xs uppercase tracking-widest text-neutral-400">Total</span>
                    <span className="font-serif-lux text-2xl text-[#e5c37a]" data-testid="carrito-total">
                      {formatearCorto(totalCents)} MXN
                    </span>
                  </div>

                  <a
                    href={waLink(mensaje)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 flex items-center justify-center gap-2 bg-[#d6ad60] text-[#050505] text-xs uppercase tracking-widest py-4 hover:bg-[#e5c37a] transition-colors"
                    data-testid="carrito-whatsapp"
                  >
                    <WhatsAppIcon className="w-4 h-4" /> Proceder al pago por WhatsApp
                  </a>

                  {/* Se dice qué va a pasar ANTES de tocar el botón: el visitante no
                      está comprando acá, está abriendo una conversación. */}
                  <p className="text-[11px] text-neutral-600 text-center leading-relaxed">
                    Se abre WhatsApp con tu pedido escrito. El pago lo coordinás directo
                    con Odyscents.
                  </p>

                  <button
                    type="button"
                    onClick={onVaciar}
                    className="w-full text-[11px] uppercase tracking-widest text-neutral-600 hover:text-neutral-300 transition-colors pt-1"
                    data-testid="carrito-vaciar"
                  >
                    Vaciar selección
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
